import { useState } from 'react'
import {
  usePromotionEvents,
  usePromotionStats,
  usePromotionRules,
  useEvaluatePromotions,
  type EventFilters,
} from '@/api/promotions'
import { useApproveEvent, useRejectEvent, useApplyEvent, useBatchDecision } from '@/api/inbox'
import { useRollbackEvent, RECONCILE_REQUIRED_PREFIX } from '@/api/automation'
import { ApiError } from '@/api/client'
import { Check, X, Play, Zap, Undo2, ArrowRight, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import Pagination from '@/components/Pagination'
import InboxHint from '@/components/InboxHint'

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  rejected: 'bg-zinc-100 text-zinc-500',
  applied: 'bg-emerald-100 text-emerald-800',
  auto_applied: 'bg-sky-100 text-sky-800',
  rolled_back: 'bg-orange-100 text-orange-800',
}

const stageLabels: Record<string, string> = {
  auto: 'Auto',
  broad: 'Broad',
  phrase: 'Phrase',
  exact: 'Exact',
  negative: 'Negative',
  bid_increase: 'Bid +',
  bid_decrease: 'Bid -',
  any: 'Alle',
}

function fmt(val: number, type: 'currency' | 'percent' | 'number' = 'number'): string {
  if (type === 'currency') return `${val.toFixed(2)} EUR`
  if (type === 'percent') return `${(val * 100).toFixed(1)}%`
  return String(val)
}

function BidComparisonCell({
  currentBid,
  suggestedBid,
  bidDelta,
}: {
  currentBid: string | null
  suggestedBid: string | null
  bidDelta: number | null
}) {
  if (!currentBid && !suggestedBid) return <span className="text-[var(--muted-foreground)]">—</span>
  const cur = currentBid ? parseFloat(currentBid) : null
  const sug = suggestedBid ? parseFloat(suggestedBid) : null

  if (cur === null && sug !== null) return <span className="tabular-nums">{sug.toFixed(2)} €</span>
  if (cur !== null && sug === null) return <span className="tabular-nums">{cur.toFixed(2)} €</span>
  if (cur === null || sug === null) return <span className="text-[var(--muted-foreground)]">—</span>

  const arrowCls = bidDelta !== null && bidDelta > 0
    ? 'text-emerald-600'
    : bidDelta !== null && bidDelta < 0
      ? 'text-red-600'
      : 'text-[var(--muted-foreground)]'
  const ArrowIcon = bidDelta !== null && bidDelta > 0 ? ArrowUp : bidDelta !== null && bidDelta < 0 ? ArrowDown : ArrowRight
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <span>{cur.toFixed(2)}</span>
      <ArrowIcon className={cn('h-3 w-3', arrowCls)} />
      <span className="font-medium">{sug.toFixed(2)}</span>
      {bidDelta !== null && bidDelta !== 0 && (
        <span className={cn('text-[10px]', arrowCls)}>
          {bidDelta > 0 ? '+' : ''}{bidDelta.toFixed(1)}%
        </span>
      )}
    </span>
  )
}

export default function PromotionList() {
  const [filters, setFilters] = useState<EventFilters>({ limit: 25, offset: 0 })
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // Scheibe S: „bei Amazon ausgefuehrt, lokal offen" — steht, bis der Operator den
  // naechsten Rollback ausloest. Ein Toast waere hier zu fluechtig: die Aussage
  // entscheidet darueber, ob er die Aktion wiederholen darf.
  const [rollbackBefund, setRollbackBefund] = useState<{ eventId: number; text: string } | null>(null)

  const { data, isLoading } = usePromotionEvents(filters)
  const { data: statsData } = usePromotionStats()
  const { data: rulesData } = usePromotionRules()
  const approve = useApproveEvent()
  const reject = useRejectEvent()
  const apply = useApplyEvent()
  const rollback = useRollbackEvent()
  const batch = useBatchDecision()
  const evaluate = useEvaluatePromotions()

  function isWithin24h(appliedAt: string | null): boolean {
    if (!appliedAt) return false
    const applied = new Date(appliedAt)
    const windowEnd = new Date(applied.getTime() + 24 * 60 * 60 * 1000)
    return new Date() < windowEnd
  }

  async function handleRollback(eventId: number) {
    const reason = prompt('Rollback-Grund:')
    if (!reason) return
    setRollbackBefund(null)
    try {
      await rollback.mutateAsync({ eventId, reason })
    } catch (err) {
      // Scheibe S (REST-Q): Dieser Fall ist NICHT „Rollback fehlgeschlagen". Bei Amazon
      // ist die Ruecknahme ausgefuehrt; offen ist nur der lokale Stand. Ein zweiter
      // Versuch schickte sie erneut an Amazon. Der Hinweis informiert deshalb und
      // handelt nicht — eine Reconcile-Aktion in der UI waere eine andere Entscheidung
      // (dieselbe Grenze wie bei D-A5).
      //
      // Erkannt am PRAEFIX und nicht an `reconcileRequired`: die Einzelroute antwortet
      // bei `success:false` mit `nack(400, error)` und sendet das Ergebnisobjekt gar
      // nicht mit (automation/routes.ts:51). Das Feld gaebe es nur im 200-Body der
      // Batch-Route. Das Praefix wird backendseitig an genau einer Stelle gebildet
      // (automation/rollback.ts, settleFailureResult); gegen Drift sichert
      // backend/tests/rollout/fehleranzeige-praefix.test.ts beide Seiten ab.
      if (err instanceof ApiError && err.message.startsWith(RECONCILE_REQUIRED_PREFIX)) {
        setRollbackBefund({ eventId, text: err.message })
      }
      // Jeder andere Fehler bleibt beim gewohnten globalen Toast (MutationCache in
      // main.tsx, laeuft bereits gelaufen). BEWUSST kein Re-throw: `handleRollback`
      // wird aus einem onClick ohne await gerufen — ein Re-throw erzeugte eine
      // unbehandelte Promise-Rejection, ohne irgendetwas zusaetzlich anzuzeigen.
    }
  }

  const events = data?.events ?? []
  const total = data?.total ?? 0
  const stats = statsData?.stats
  const rules = rulesData?.rules ?? []

  const page = Math.floor((filters.offset ?? 0) / (filters.limit ?? 25))

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function toggleAll() {
    const pending = events.filter(e => e.status === 'pending')
    if (selected.size === pending.length) setSelected(new Set())
    else setSelected(new Set(pending.map(e => e.id)))
  }

  async function handleBatch(action: 'approve' | 'reject') {
    if (selected.size === 0) return
    await batch.mutateAsync({ action, ids: [...selected] })
    setSelected(new Set())
  }

  return (
    <div className="space-y-4">
      <InboxHint className="mb-3" />

      {/* Scheibe S (REST-Q): Gegenstueck zum Banner in `CampaignList` — hier fuer die
          EINZEL-Antwort des Rollbacks. Der Fall ist kein gewoehnlicher Fehlschlag:
          Amazon hat die Ruecknahme ausgefuehrt, nur die lokale Zeile fehlt. Wer ihn
          fuer einen Fehlschlag haelt, wiederholt ihn. */}
      {rollbackBefund && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {`Event #${rollbackBefund.eventId}: bei Amazon zurückgerollt, lokal nicht verbucht — NICHT wiederholen, lokalen Stand abgleichen. (${rollbackBefund.text})`}
          </span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            { label: 'Pending', value: stats.pending, color: 'text-amber-600' },
            { label: 'Approved', value: stats.approved, color: 'text-blue-600' },
            { label: 'Applied', value: stats.applied, color: 'text-emerald-600' },
            { label: 'Rejected', value: stats.rejected, color: 'text-zinc-500' },
            { label: 'Letzte 7d', value: stats.last7d, color: 'text-[var(--foreground)]' },
          ].map(s => (
            <div key={s.label} className="rounded-md border border-[var(--border)] bg-[var(--card)] p-3 text-center">
              <p className="text-xs text-[var(--muted-foreground)]">{s.label}</p>
              <p className={cn('text-xl font-bold', s.color)}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filters.status ?? ''}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value || undefined, offset: 0 }))}
          className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
        >
          <option value="">Alle Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="applied">Applied</option>
          <option value="auto_applied">Auto-Applied</option>
          <option value="rolled_back">Rolled Back</option>
        </select>

        <select
          value={filters.ruleId ?? ''}
          onChange={e => setFilters(f => ({ ...f, ruleId: e.target.value ? Number(e.target.value) : undefined, offset: 0 }))}
          className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
        >
          <option value="">Alle Rules</option>
          {rules.map(r => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>

        <div className="ml-auto flex gap-2">
          {selected.size > 0 && (
            <>
              <button onClick={() => handleBatch('approve')} disabled={batch.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                <Check className="h-3.5 w-3.5" />Approve ({selected.size})
              </button>
              <button onClick={() => handleBatch('reject')} disabled={batch.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                <X className="h-3.5 w-3.5" />Reject ({selected.size})
              </button>
            </>
          )}
          <button onClick={() => evaluate.mutate()} disabled={evaluate.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-xs font-medium hover:bg-[var(--accent)] disabled:opacity-50">
            <Zap className="h-3.5 w-3.5" />{evaluate.isPending ? 'Evaluiere...' : 'Evaluate'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
        <table className="min-w-full text-sm">
          <thead className="border-b border-[var(--border)] bg-[var(--muted)]">
            <tr>
              <th className="px-3 py-2 text-left">
                <input type="checkbox" checked={selected.size > 0 && selected.size === events.filter(e => e.status === 'pending').length}
                  onChange={toggleAll} className="rounded" />
              </th>
              <th className="px-3 py-2 text-left font-medium">Search Term</th>
              <th className="px-3 py-2 text-left font-medium">Rule</th>
              <th className="px-3 py-2 text-left font-medium">Flow</th>
              <th className="px-3 py-2 text-right font-medium">Clicks</th>
              <th className="px-3 py-2 text-right font-medium">Orders</th>
              <th className="px-3 py-2 text-right font-medium">ACoS</th>
              <th className="px-3 py-2 text-right font-medium">Spend</th>
              <th className="px-3 py-2 text-left font-medium">Bid (Akt → Vorschl.)</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Datum</th>
              <th className="px-3 py-2 text-center font-medium">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Lade...</td></tr>
            )}
            {!isLoading && events.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-[var(--muted-foreground)]">Keine Events vorhanden</td></tr>
            )}
            {events.map(event => (
              <tr key={event.id} className="border-b border-[var(--border)] hover:bg-[var(--muted)]/50">
                <td className="px-3 py-2">
                  {event.status === 'pending' && (
                    <input type="checkbox" checked={selected.has(event.id)} onChange={() => toggleSelect(event.id)} className="rounded" />
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs max-w-[200px] truncate" title={event.searchTerm ?? ''}>
                  {event.searchTerm}
                </td>
                <td className="px-3 py-2 text-xs">{event.ruleName}</td>
                <td className="px-3 py-2 text-xs">
                  <span className="text-[var(--muted-foreground)]">{stageLabels[event.ruleFromStage] ?? event.ruleFromStage}</span>
                  {' → '}
                  <span className="font-medium">{stageLabels[event.ruleToStage] ?? event.ruleToStage}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{event.metrics?.clicks ?? '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{event.metrics?.orders ?? '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{event.metrics?.acos != null ? fmt(event.metrics.acos, 'percent') : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{event.metrics?.spend != null ? fmt(event.metrics.spend, 'currency') : '—'}</td>
                <td className="px-3 py-2 text-xs">
                  <BidComparisonCell currentBid={event.currentBid} suggestedBid={event.suggestedBid} bidDelta={event.bidDelta} />
                </td>
                <td className="px-3 py-2">
                  <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize', statusColors[event.status])}>
                    {event.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
                  {new Date(event.createdAt).toLocaleDateString('de-DE')}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {event.status === 'pending' && (
                      <>
                        <button onClick={() => approve.mutate({ id: event.id })} disabled={approve.isPending}
                          className="rounded p-1 text-emerald-600 hover:bg-emerald-50" title="Genehmigen">
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => reject.mutate({ id: event.id })} disabled={reject.isPending}
                          className="rounded p-1 text-red-600 hover:bg-red-50" title="Ablehnen">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {event.status === 'approved' && (
                      <button onClick={() => apply.mutate({ id: event.id })} disabled={apply.isPending}
                        className="rounded p-1 text-blue-600 hover:bg-blue-50" title="Ausfuehren">
                        <Play className="h-4 w-4" />
                      </button>
                    )}
                    {(event.status === 'applied' || event.status === 'auto_applied') && isWithin24h(event.appliedAt) && (
                      <button onClick={() => handleRollback(event.id)} disabled={rollback.isPending}
                        className="rounded p-1 text-orange-600 hover:bg-orange-50" title="Rollback (24h)">
                        <Undo2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={filters.limit ?? 25}
        totalItems={total}
        onPageChange={(p) => setFilters((f) => ({ ...f, offset: p * (f.limit ?? 25) }))}
        onPageSizeChange={(s) => setFilters((f) => ({ ...f, limit: s, offset: 0 }))}
      />
    </div>
  )
}
